import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL, API_ENDPOINTS, getAuthHeaders } from '../config/api';
import toast from 'react-hot-toast';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));

  // Set up axios interceptor for authentication
  useEffect(() => {
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      localStorage.setItem('token', token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      localStorage.removeItem('token');
    }
  }, [token]);

  // Check if user is authenticated on app load
  useEffect(() => {
    const checkAuth = async () => {
      if (token) {
        try {
          const response = await axios.get(`${API_BASE_URL}${API_ENDPOINTS.PROFILE}`, {
            headers: getAuthHeaders()
          });
          setUser(response.data.user);
        } catch (error) {
          console.error('Auth check failed:', error);
          // Don't logout on API failure, just clear the token and continue
          setToken(null);
          setUser(null);
        }
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [token]);

  const login = async (email, password) => {
    try {
      console.log('🔐 Login attempt:', { email, password });
      console.log('🌐 API URL:', `${API_BASE_URL}${API_ENDPOINTS.LOGIN}`);
      
      const requestData = {
        email,
        password
      };
      
      console.log('📤 Request Data:', requestData);
      console.log('📤 Request Headers:', {
        'Content-Type': 'application/json'
      });
      
      const response = await axios.post(`${API_BASE_URL}${API_ENDPOINTS.LOGIN}`, requestData, {
        headers: {
          'Content-Type': 'application/json'
        }
      });

      console.log('📡 API Response:', response);
      console.log('📊 Response Data:', response.data);
      
      // Handle the mock API response structure
      const { token: newToken, user: userData } = response.data;
      
      console.log('🔑 Extracted Token:', newToken);
      console.log('👤 Extracted User:', userData);
      
      // Persist the token SYNCHRONOUSLY here, before any state update or
      // navigation. Requests fired immediately after login (dashboard stats,
      // company profile, feature flags — they read the token from localStorage
      // via getAuthHeaders) would otherwise race ahead of the [token] useEffect
      // that writes localStorage on the next render, get a 401, and trip the
      // session-timeout guard that bounces the user back to login.
      if (newToken) {
        localStorage.setItem('token', newToken);
        axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      }
      setToken(newToken);
      setUser(userData);

      console.log('✅ Login successful - Token and user set');
      toast.success('Login successful!');
      return { success: true, user: userData, token: newToken };
    } catch (error) {
      console.error('❌ Login Error:', error);
      console.error('❌ Error Response:', error.response);
      console.error('❌ Error Status:', error.response?.status);
      console.error('❌ Error Status Text:', error.response?.statusText);
      console.error('❌ Error Data:', error.response?.data);
      console.error('❌ Error Message:', error.message);
      console.error('❌ Request Config:', error.config);
      
      const errorMessage = error.response?.data?.error || error.response?.data?.message || 'Login failed';
      console.error('❌ Final Error Message:', errorMessage);
      
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    toast.success('Logged out successfully');
  };

  const updateProfile = async (profileData) => {
    try {
      const response = await axios.put(`${API_BASE_URL}${API_ENDPOINTS.PROFILE}`, profileData, {
        headers: getAuthHeaders()
      });
      
      setUser(response.data.user);
      toast.success('Profile updated successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Profile update failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.put(`${API_BASE_URL}${API_ENDPOINTS.CHANGE_PASSWORD}`, {
        currentPassword,
        newPassword
      }, {
        headers: getAuthHeaders()
      });
      
      toast.success('Password changed successfully');
      return { success: true };
    } catch (error) {
      const errorMessage = error.response?.data?.error || 'Password change failed';
      toast.error(errorMessage);
      return { success: false, error: errorMessage };
    }
  };

  const hasRole = (requiredRoles) => {
    if (!user || !user.roles) return false;
    
    if (Array.isArray(requiredRoles)) {
      return requiredRoles.some(role => user.roles.includes(role));
    }
    return user.roles.includes(requiredRoles);
  };

  const canAccessCustomer = (customerId) => {
    if (!user || !user.roles) return false;
    if (user.roles.includes('ADMIN') || user.roles.includes('STAFF')) return true;
    if (user.roles.includes('CUSTOMER') && user.customerId === customerId) return true;
    return false;
  };

  const value = {
    user,
    setUser,
    token,
    isLoading,
    login,
    logout,
    updateProfile,
    changePassword,
    hasRole,
    canAccessCustomer,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};
